import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client/react';

import ConversionUnitForm from '@atom/components/common/conversionUnitsTable/ConversionUnitForm';
import ConversionUnitsTable from '@atom/components/common/conversionUnitsTable/ConversionUnitsTable';
import {
  addConversionUnit,
  removeConversionUnit,
  setConversionUnitActive,
  setDefaultConversionUnit,
  sortConversionUnits,
  updateConversionUnit,
} from '@atom/components/common/conversionUnitsTable/conversionUnitUtilities';
import DeleteModal from '@atom/components/common/DeleteModal';
import SchemaDetailContext from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  SCHEMA_CONVERSION_UNIT_CREATE,
  SCHEMA_CONVERSION_UNIT_DELETE_BULK,
  SCHEMA_CONVERSION_UNIT_UPDATE,
} from '@atom/graph/schema';
import { Progress, Snackbar } from '@atom/mui';
import { budgetUnitsSelector } from '@atom/selectors/preferencesSelectors';
import {
  ConversionUnit,
  SchemaConversionUnitUpdateInput,
} from '@atom/types/schema';

import {
  isPublishedConversionUnit,
  toPendingConversionUnits,
} from './costDetailUtilities';

import classes from './costDetail.module.css';

type ConversionUnitsSectionProps = {
  /**
   * the schema's unit that each conversion unit converts to
   */
  baseUnit: string;
};

/**
 * draft schemas save each change right away. Published schemas only apply
 * changes on publish, so they're held in pendingConversionUnits until then.
 */
const ConversionUnitsSection = ({ baseUnit }: ConversionUnitsSectionProps) => {
  const {
    schemaTree,
    refetchSchemaTree,
    pendingConversionUnits,
    setPendingConversionUnits,
  } = useContext(SchemaDetailContext);
  const budgetUnits = useSelector(budgetUnitsSelector);

  // 'new' while adding a row, or the row being edited
  const [formTarget, setFormTarget] = useState<'new' | ConversionUnit | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<ConversionUnit | null>(null);

  const [createConversionUnit, { loading: loadingCreate }] = useMutation(
    SCHEMA_CONVERSION_UNIT_CREATE,
  );
  const [updateConversionUnitMutation, { loading: loadingUpdate }] =
    useMutation(SCHEMA_CONVERSION_UNIT_UPDATE);
  const [deleteConversionUnits, { loading: loadingDelete }] = useMutation(
    SCHEMA_CONVERSION_UNIT_DELETE_BULK,
  );

  const loading = loadingCreate || loadingUpdate || loadingDelete;
  const isPublished = schemaTree?.isPublished;
  const savedConversionUnits = schemaTree?.budget?.conversionUnits ?? [];
  const conversionUnits = sortConversionUnits(
    isPublished
      ? (pendingConversionUnits ?? savedConversionUnits)
      : savedConversionUnits,
  );

  const updatePending = (
    update: (units: ConversionUnit[]) => ConversionUnit[],
  ) =>
    setPendingConversionUnits(current =>
      toPendingConversionUnits(
        update(current ?? savedConversionUnits),
        savedConversionUnits,
      ),
    );

  const saveDraft = async (save: () => Promise<unknown>) => {
    try {
      await save();
    } catch (error) {
      Snackbar.error({ message: 'Failed to save conversion units.' });
    }

    refetchSchemaTree();
  };

  const updateDraft = (
    conversionUnitId: string,
    changes: Omit<
      SchemaConversionUnitUpdateInput,
      'schemaId' | 'conversionUnitId'
    >,
  ) =>
    updateConversionUnitMutation({
      variables: {
        input: { schemaId: schemaTree?.id, conversionUnitId, ...changes },
      },
    });

  const submitConversionUnit = (unit: string, conversionNumber: number) => {
    if (isPublished) {
      updatePending(units =>
        formTarget === 'new'
          ? addConversionUnit(units, unit, conversionNumber)
          : updateConversionUnit(units, formTarget.id, {
              unit,
              conversionNumber,
            }),
      );
    } else {
      saveDraft(() =>
        formTarget === 'new'
          ? createConversionUnit({
              variables: {
                input: { schemaId: schemaTree?.id, unit, conversionNumber },
              },
            })
          : updateDraft(formTarget.id, { unit, conversionNumber }),
      );
    }

    setFormTarget(null);
  };

  const changeDefault = (id: string) => {
    if (isPublished) {
      updatePending(units => setDefaultConversionUnit(units, id));
    } else {
      // the default can't be inactive, matching setDefaultConversionUnit
      saveDraft(() => updateDraft(id, { isDefault: true, active: true }));
    }
  };

  const changeActive = (id: string, active: boolean) => {
    if (isPublished) {
      updatePending(units => setConversionUnitActive(units, id, active));
    } else {
      saveDraft(() => updateDraft(id, { active }));
    }
  };

  const confirmDelete = () => {
    const { id } = deleteTarget;

    if (isPublished) {
      updatePending(units => removeConversionUnit(units, id));
    } else {
      // a lone remaining unit becomes the default, matching removeConversionUnit
      const remaining = conversionUnits.filter(unit => unit.id !== id);
      const newDefault =
        remaining.length === 1 && !remaining[0].isDefault ? remaining[0] : null;

      saveDraft(async () => {
        await deleteConversionUnits({
          variables: {
            input: { schemaId: schemaTree?.id, conversionUnitIds: [id] },
          },
        });

        if (newDefault) {
          await updateDraft(newDefault.id, { isDefault: true });
        }
      });
    }

    if (formTarget !== 'new' && formTarget?.id === id) {
      setFormTarget(null);
    }

    setDeleteTarget(null);
  };

  const canModify = (conversionUnit: ConversionUnit) =>
    !isPublished || !isPublishedConversionUnit(conversionUnit.id, schemaTree);

  const deleteMsg = [
    'Are you sure you want to delete the conversion unit',
    !!deleteTarget
      ? `${deleteTarget.unit.toUpperCase()} (1 ${deleteTarget.unit.toUpperCase()} = ${deleteTarget.conversionNumber} ${baseUnit.toUpperCase()})`
      : '',
    '?',
  ].join(' ');

  return (
    <div className={classes.costContainer}>
      <div className={classes.sectionTitle}>
        <div>Conversion Units</div>
        {loading && <Progress size={20} thickness={2} />}
      </div>
      <ConversionUnitsTable
        conversionUnits={conversionUnits}
        baseUnit={baseUnit}
        showAddedColumns
        onDefaultChange={changeDefault}
        onActiveChange={changeActive}
        onEdit={setFormTarget}
        onDelete={id =>
          setDeleteTarget(conversionUnits.find(unit => unit.id === id))
        }
        canModify={canModify}
        onAdd={() => setFormTarget('new')}
        addDisabled={!baseUnit || loading}
        renderForm={
          formTarget !== null &&
          (columnCount => (
            <ConversionUnitForm
              key={formTarget === 'new' ? 'new' : formTarget.id}
              conversionUnits={conversionUnits}
              unitOptions={budgetUnits}
              baseUnit={baseUnit}
              columnCount={columnCount}
              conversionUnit={formTarget === 'new' ? undefined : formTarget}
              onSubmit={submitConversionUnit}
              onCancel={() => setFormTarget(null)}
              dataCy="schemaDetailConversionUnitForm"
            />
          ))
        }
        dataCy="schemaDetailConversionUnitsTable"
      />
      <DeleteModal
        open={!!deleteTarget}
        title="Delete Conversion Unit?"
        content={deleteMsg}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        dataCy="schemaDetailConversionUnitDeleteModal"
      />
    </div>
  );
};

export default ConversionUnitsSection;
